import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import AppStoreLink from "@Components/atoms/AppStoreLink";
import useCheerzApiProvider from "@Hooks/useCheerzApiProvider";
import { ReactComponent as IconFsc } from "@Icons/fsc.svg";
import { ReactComponent as IconHeart } from "@Icons/heart.svg";
import { ReactComponent as IconLock } from "@Icons/lock.svg";
import { ReactComponent as IconNeutralClimate } from "@Icons/neutral-climate.svg";
import { ReactComponent as IconTruck } from "@Icons/truck.svg";
import enFlagLogo from "@Images/flag-en.png";
import esFlagLogo from "@Images/flag-es.png";
import frFlagLogo from "@Images/flag-fr.png";
import itFlagLogo from "@Images/flag-it.png";
import mastercardLogo from "@Images/mastercard.png";
import paypalLogo from "@Images/paypal.png";
import visaLogo from "@Images/visa.png";
import { getPathChangingLocale, Locale, LOCALES } from "@Locales/index";
import { Currency } from "@Types/api/price";
import { Image } from "@Types/images";
import { FooterLink, SocialLinkByLocales } from "@Types/links";
import { deliveryCompagnies } from "@Utils/delivery";
import { commitmentsLinks, confidentialityLink, fscLinks, socialLinks } from "@Utils/links";
import { convertFromApiToUserContent } from "@Utils/user";
import "./style.scss";

interface Props {
  currency: Currency;
}

export const Footer: FunctionComponent<Props> = ({ currency }) => {
  const intl = useIntl();
  const freeDeliveryAmount = intl.formatNumber(50, {
    currency,
    minimumFractionDigits: 0,
    style: "currency",
  });

  const aboutLinks: FooterLink[] = [
    { name: "join", target: "_blank", url: "https://cheerz.welcomekit.co/" },
    { name: "legals", target: "_self", url: `https://www.cheerz.com/${intl.locale}/pages/legal` },
    {
      name: "terms",
      target: "_self",
      url: `https://www.cheerz.com/${intl.locale}/pages/terms-and-conditions`,
    },
    {
      name: "confidentiality",
      target: "_blank",
      url: confidentialityLink,
    },
  ];
  const helpLinks: FooterLink[] = [
    {
      name: "contact",
      target: "_self",
      url: `https://www.cheerz.com/${intl.locale}/pages/contact`,
    },
    { name: "faq", target: "_blank", url: "http://support.cheerz.com/hc/" },
    {
      name: "promo_code",
      target: "_self",
      url: `https://www.cheerz.com/${intl.locale}/categories/code-promo-cheerz`,
    },
    {
      name: "how_create_album",
      target: "_self",
      url: "https://royal.cheerz.com/learn/comment-creer-un-album-photo-en-ligne-le-guide",
    },
  ];
  const paymentTypes = [
    { name: "Mastercard", src: mastercardLogo },
    { name: "Visa", src: visaLogo },
    { name: "Paypal", src: paypalLogo },
  ];
  const productsLinks: FooterLink[] = [
    { name: "all", url: `https://www.cheerz.com/${intl.locale}/categories/all` },
    { name: "books", url: `https://www.cheerz.com/${intl.locale}/categories/books` },
    { name: "prints", url: `https://www.cheerz.com/${intl.locale}/categories/prints` },
    { name: "canvas", url: `https://www.cheerz.com/${intl.locale}/categories/canvas` },
    {
      name: "cheap_books",
      url: `https://www.cheerz.com/${intl.locale}/products/cheap-photo-album`,
    },
    {
      name: "cheap_prints",
      url: `https://www.cheerz.com/${intl.locale}/products/cheap-photo-prints`,
    },
    {
      name: "photobooth",
      url: "https://live.cheerz.com",
    },
  ];

  return (
    <div className="Footer">
      <div className="Footer-infos">
        <div className="Footer-infos-item">
          <p className="Footer-infos-item-title">
            <IconLock />
            <span>{intl.formatMessage({ id: "cheerz_Footer_infos_payment_title" })}</span>
          </p>
          {paymentTypes.map(({ name, src }: Image) => (
            <img alt={name} key={name} src={src} />
          ))}
        </div>

        <div className="Footer-infos-item">
          <p className="Footer-infos-item-title">
            <IconHeart />
            <span>{intl.formatMessage({ id: "cheerz_Footer_infos_free_delivery_title" })}</span>
          </p>
          <p>
            {intl.formatMessage(
              { id: "cheerz_Footer_infos_free_delivery_description" },
              { value: freeDeliveryAmount }
            )}
          </p>
        </div>

        <div className="Footer-infos-item">
          <p className="Footer-infos-item-title">
            <IconTruck />
            <span>{intl.formatMessage({ id: "cheerz_Footer_infos_delivery_title" })}</span>
          </p>
          {deliveryCompagnies[intl.locale].map(({ name, src }: Image) => (
            <img alt={name} key={name} src={src} />
          ))}
        </div>
      </div>

      <div className="Footer-links-wrapper">
        <div className="Footer-links">
          <div className="Footer-links-products">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_products_title" })}
            </p>
            {productsLinks.map((link: FooterLink) => (
              <a
                className="Footer-links-link"
                href={link.url}
                key={link.name}
                rel="noopener noreferrer"
              >
                {intl.formatMessage({ id: `cheerz_Footer_links_products_${link.name}` })}
              </a>
            ))}
          </div>

          <div className="Footer-links-about">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_about_title" })}
            </p>
            {intl.locale === "fr" && (
              <a
                className="Footer-links-link"
                href="https://royal.cheerz.com/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Le blog
              </a>
            )}
            <a
              className="Footer-links-link"
              href={commitmentsLinks[intl.locale]}
              rel="noopener noreferrer"
            >
              {intl.formatMessage({ id: "cheerz_Footer_links_about_commitments" })}
            </a>
            {aboutLinks.map((link: FooterLink) => (
              <a
                className="Footer-links-link"
                href={link.url}
                key={link.name}
                rel="noopener noreferrer"
                target={link.target}
              >
                {intl.formatMessage({ id: `cheerz_Footer_links_about_${link.name}` })}
              </a>
            ))}
          </div>

          <div className="Footer-links-help">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_help_title" })}
            </p>
            {helpLinks.map((link: FooterLink) => {
              let href = link.url;
              if (link.name === "faq") {
                href = `${link.url}${intl.locale}`;
              }

              return (
                <a
                  className="Footer-links-link"
                  href={href}
                  key={link.name}
                  rel="noopener noreferrer"
                  target={link.target}
                >
                  {intl.formatMessage({ id: `cheerz_Footer_links_help_${link.name}` })}
                </a>
              );
            })}
          </div>

          <div className="Footer-links-commitments">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_commitments_title" })}
            </p>
            <a
              className="Footer-links-commitments-item"
              href={fscLinks[intl.locale]}
              rel="noopener noreferrer"
              target="_blank"
            >
              <IconFsc />
              <span>{intl.formatMessage({ id: "cheerz_Footer_links_commitments_fsc" })}</span>
            </a>
            <div className="Footer-links-commitments-item">
              <IconNeutralClimate />
              <span>
                {intl.formatMessage({ id: "cheerz_Footer_links_commitments_neutral_climate" })}
              </span>
            </div>
          </div>

          <div className="Footer-links-connecting">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_connecting_title" })}
            </p>
            {socialLinks.map((socialLink: SocialLinkByLocales) => {
              const href =
                socialLink.name === "Facebook"
                  ? socialLink.url[intl.locale]
                  : socialLink.name === "Twitter"
                  ? socialLink.url[intl.locale] || socialLink.url.world
                  : socialLink.url.world;

              return (
                <a href={href} key={socialLink.name} rel="noopener noreferrer" target="_blank">
                  <img alt={socialLink.name} src={socialLink.src} title={socialLink.name} />
                </a>
              );
            })}
          </div>
          <div className="Footer-links-appStoreLinks">
            <AppStoreLink store="apple" />
            <AppStoreLink store="google" />
          </div>

          <div className="Footer-links-languages">
            {LOCALES.map((locale: Locale) => {
              const language = {
                en: { name: "English", src: enFlagLogo },
                es: { name: "Español", src: esFlagLogo },
                fr: { name: "Français", src: frFlagLogo },
                it: { name: "Italiano", src: itFlagLogo },
              };

              return (
                <a
                  className="Footer-links-link"
                  href={getPathChangingLocale(locale)}
                  key={language[locale].name}
                  rel="noopener noreferrer"
                >
                  <img
                    alt={language[locale].name}
                    height="18"
                    src={language[locale].src}
                    title={language[locale].name}
                    width="18"
                  />
                  {language[locale].name}
                </a>
              );
            })}
          </div>

          <div className="Footer-links-copyright">
            <p>Copyright {new Date().getFullYear()} Cheerz</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const FooterContainer: FunctionComponent = () => {
  const [currency, setCurrency] = useState<Currency>("EUR");
  const cheerzApiProvider = useCheerzApiProvider();

  useEffect(() => {
    cheerzApiProvider
      .getUser()
      .then((userResponse) => setCurrency(convertFromApiToUserContent(userResponse).currency))
      .catch(() => {
        return;
      });
  }, [cheerzApiProvider]);

  return <Footer currency={currency}></Footer>;
};

export default FooterContainer;
